import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex, Grid, Tab, TabList, Tabs } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { PlansVariant } from '__generated__/GQL';
import useRect from 'common/hooks/useRect';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';
import OnboardingFullPage from 'features/Onboarding/components/OnboardingFullPage';

import { OnboardingPlanDisplay } from '../../types';
import PlanCards from './components/PlanCards';
import PlanDetails from './components/PlanDetails';
import locales from './locales';

interface PlansDesktopProps {
  onboardingPlanDisplays: OnboardingPlanDisplay[];
  onClose: () => void;
  referrer?: string;
  onChoosePlanDisplay: (plan: OnboardingPlanDisplay) => void;
  variant: PlansVariant | null;
  setVariant: (variant: PlansVariant | null) => void;
  shouldShowVariantSelector: boolean;
  shouldShowPackagePriceWithVAT: boolean;
  selectedPlanId: string | null;
}

const PlansDesktop: FC<PlansDesktopProps> = ({
  onboardingPlanDisplays,
  onChoosePlanDisplay,
  onClose,
  referrer,
  selectedPlanId,
  setVariant,
  shouldShowPackagePriceWithVAT,
  shouldShowVariantSelector,
  variant,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const detailsRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [tabsRect, computeTabsRect] = useRect(tabsRef);
  const [tableRect, computeTableRect] = useRect(tableRef);
  const [buttonRect, computeButtonRect] = useRect(buttonRef);

  useLayoutEffect(() => {
    computeTabsRect();
    computeTableRect();
    computeButtonRect();
  }, [showDetails, computeTabsRect, computeTableRect, computeButtonRect]);

  useLayoutEffect(() => {
    if (showDetails) {
      detailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showDetails]);

  useEffect(() => {
    if (!variant && shouldShowVariantSelector) {
      setVariant(PlansVariant.Solo);
    }
  }, [variant, setVariant, shouldShowVariantSelector]);

  const isLoading = onboardingPlanDisplays.length === 0;

  const handleShowDetails = () => {
    setShowDetails((value) => !value);
  };

  const handleVariantSwitch = (newVariant: PlansVariant) => {
    if (variant === newVariant) {
      return;
    }

    logEvent({
      name: 'Price Plan Variant Switched',
      properties: {
        from: variant?.toLowerCase(),
        to: newVariant.toLocaleLowerCase(),
      },
    });

    setVariant(newVariant);
  };

  return (
    <OnboardingFullPage data-testid="plan-choice" ref={titleRef}>
      <Flex
        justifyContent="center"
        marginTop="4rem"
        paddingTop="space-32"
        width="100vw"
      >
        <Box width="80%">
          <Heading
            marginBottom={shouldShowVariantSelector ? 'space-16' : 'space-32'}
            marginLeft="24%"
            size="lg"
          >
            {locales.title}
          </Heading>
          <Box alignItems="center" display="flex" flexDirection="column">
            <Grid
              columnGap="space-32"
              position="relative"
              ref={tableRef}
              templateColumns={`250px repeat(${
                onboardingPlanDisplays.length || 1
              }, 292px)`}
            >
              <Box gridColumnEnd={-1} gridColumnStart={2} ref={tabsRef}>
                {shouldShowVariantSelector ? (
                  <Tabs
                    alignItems="center"
                    as={Flex}
                    flexDirection="column"
                    marginBottom="space-32"
                    outline="none"
                    width={{ base: '100%', md: 'initial' }}
                  >
                    <Box
                      borderBottom="1px solid"
                      borderColor="grey.4"
                      paddingX={{ md: 'space-24' }}
                      width={{ base: '100%', md: 'initial' }}
                    >
                      <TabList
                        display="grid"
                        gridTemplateColumns="repeat(2, 1fr)"
                        justifyContent="center"
                        position="relative"
                        width={{ md: `18rem` }}
                      >
                        <Tab
                          onClick={() => handleVariantSwitch(PlansVariant.Solo)}
                          paddingY="space-12"
                        >
                          {locales.variantSolo}
                        </Tab>
                        <Tab
                          onClick={() => handleVariantSwitch(PlansVariant.Team)}
                          paddingY="space-12"
                        >
                          {locales.variantTeam}
                        </Tab>
                        <Box
                          animate={{
                            transition: {
                              duration: 0.2,
                              stiffness: 40,
                              type: 'spring',
                            },
                            x: `${
                              (variant === PlansVariant.Team ? 1 : 0) * 100
                            }%`,
                          }}
                          as={motion.div}
                          background="grey.800"
                          borderRadius="radius-full"
                          bottom={0}
                          height="0.25rem"
                          initial={false}
                          left={0}
                          position="absolute"
                          width={{ base: 1 / 2, md: '9rem' }}
                        />
                      </TabList>
                    </Box>
                    {isLoading ? (
                      <Box marginTop="space-64">
                        <Loader />
                      </Box>
                    ) : null}
                  </Tabs>
                ) : null}
              </Box>
              {isLoading ? null : (
                <>
                  <PlanCards
                    highlightHeight={
                      showDetails
                        ? tableRect.height - tabsRect.height - buttonRect.height
                        : 0
                    }
                    onboardingPlans={onboardingPlanDisplays}
                    onChoose={onChoosePlanDisplay}
                    onClose={onClose}
                    referrer={referrer}
                    selectedPlanId={selectedPlanId}
                    shouldShowPackagePriceWithVAT={
                      shouldShowPackagePriceWithVAT
                    }
                    showDetails={showDetails}
                    variant={variant}
                  />
                  {showDetails ? (
                    <PlanDetails
                      onboardingPlans={onboardingPlanDisplays}
                      ref={detailsRef}
                      variant={variant}
                    />
                  ) : null}
                </>
              )}
              {isLoading ? null : (
                <>
                  <Box
                    bottom="0"
                    gridColumnEnd={-1}
                    gridColumnStart={2}
                    ref={buttonRef}
                  >
                    <Box
                      borderTop={showDetails ? 'none' : '1px solid'}
                      borderTopColor="grey.3"
                      display="grid"
                      marginTop="space-32"
                    >
                      <Button
                        icon={showDetails ? 'chevron-up' : 'chevron-down'}
                        iconPosition="right"
                        justifySelf="center"
                        marginY="space-24"
                        onClick={handleShowDetails}
                        variant="secondary"
                        width="280px"
                      >
                        {showDetails ? locales.hideDetails : locales.seeDetails}
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Grid>
          </Box>
        </Box>
      </Flex>
    </OnboardingFullPage>
  );
};

export default PlansDesktop;
